

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const Registration = ({ onRegister, errorMessage, successMessage, onClearMessages, onNavigateToLogin }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Extract invitation token, email, and name from URL parameters
  const invitationToken = searchParams.get('token');
  const urlEmail = searchParams.get('email');
  const urlName = searchParams.get('name');

  // Pre-fill email if provided in URL (for admin invitations)
  useEffect(() => {
    if (urlEmail) {
      setEmail(urlEmail);
    }
  }, [urlEmail]);

  // Pre-fill name if provided in URL (from investor email confirmation)
  useEffect(() => {
    if (urlName) {
      setName(urlName);
    }
  }, [urlName]);

  const handleBackToLogin = () => {
    // Clear any messages when navigating back to login
    if (onNavigateToLogin) {
      onNavigateToLogin();
    } else {
      navigate('/login');
    }
  };

  // Block access if no invitation token is present
  if (!invitationToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h2 className="mt-6 text-3xl font-bold text-gray-900">
              Invitation Required
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Registration is only available through invitation. Please contact your administrator to receive an invitation link.
            </p>
            <button
              onClick={handleBackToLogin}
              className="mt-4 btn-primary"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Validate password requirements
  const validatePassword = (pwd) => {
    const errors = [];
    if (pwd.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    if (!/\d/.test(pwd)) {
      errors.push('Password must contain at least one number');
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) {
      errors.push('Password must contain at least one special character');
    }
    return errors;
  };

  // Clear messages when user starts typing
  const handleInputChange = (setter) => (e) => {
    if (onClearMessages) {
      onClearMessages();
    }
    setter(e.target.value);
  };

  // Handle password change with validation
  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    setPasswordErrors(validatePassword(newPassword));
  };


  const handleSubmit = async (event) => {
    event.preventDefault();

    // Check password requirements
    const currentPasswordErrors = validatePassword(password);
    if (currentPasswordErrors.length > 0) {
      alert('Please fix the following password requirements:\n' + currentPasswordErrors.join('\n'));
      return;
    }

    // Check if passwords match
    if (password !== confirmPassword) {
      // You might want to add a local error state here instead of using the global error
      alert('Passwords do not match. Please try again.');
      return;
    }

    setIsLoading(true);
    
    let registrationData = {
      username: email, // Use email as username
      email,
      name,
      password,
      accountType: 'client', // Default to client, backend will determine actual type from invitation
    };

    // Add invitation token if present (for both admin and client invitations)
    if (invitationToken) {
      registrationData.invitationToken = invitationToken;
    }

    try {
      await onRegister(registrationData);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
            Create your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Set up your StreamLaunch account
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={handleInputChange(setEmail)}
                className="input-field mt-1"
                placeholder={invitationToken ? "Your invitation email address" : "Enter your work email address"}
                required
                disabled={!!urlEmail} // Disable if email is pre-filled from invitation
              />
              <p className="text-xs text-gray-500 mt-1">
                {invitationToken ? "Use the email address that received the invitation" : "This will be your username for logging in"}
              </p>
            </div>

            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Full Name
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={handleInputChange(setName)}
                className="input-field mt-1"
                placeholder="Enter your full name"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                This name will be displayed in the system
              </p>
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={handlePasswordChange}
                className="input-field mt-1"
                placeholder="Create a secure password"
                required
              />
              <div className="mt-2 space-y-1">
                <p className={`text-xs ${password.length >= 8 ? 'text-green-600' : 'text-gray-500'}`}>
                  {password.length >= 8 ? '✓' : '○'} At least 8 characters
                </p>
                <p className={`text-xs ${/\d/.test(password) ? 'text-green-600' : 'text-gray-500'}`}>
                  {/\d/.test(password) ? '✓' : '○'} Contains a number
                </p>
                <p className={`text-xs ${/[!@#$%^&*(),.?":{}|<>]/.test(password) ? 'text-green-600' : 'text-gray-500'}`}>
                  {/[!@#$%^&*(),.?":{}|<>]/.test(password) ? '✓' : '○'} Contains a special character (!@#$%^&*(),.?":{}|&lt;&gt;)
                </p>
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input-field mt-1"
                placeholder="Confirm your password"
                required
              />
              {password && confirmPassword && password !== confirmPassword && (
                <p className="text-xs text-red-500 mt-1">
                  Passwords do not match
                </p>
              )}
            </div>


            {successMessage && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-green-800 font-medium">Registration Initiated!</p>
                    <p className="text-sm text-green-700 mt-1">{successMessage}</p>
                    {!invitationToken && (
                      <p className="text-sm text-green-700 mt-2">
                        <strong>Important:</strong> You must verify your email before you can log in.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {errorMessage && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-800">{errorMessage}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div className="space-y-3">
            <button 
              type="submit" 
              disabled={isLoading}
              className={`btn-primary w-full ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {invitationToken ? 'Creating Account...' : 'Creating Account...'}
                </div>
              ) : (
                invitationToken ? 'Accept Invitation & Create Account' : 'Create Account'
              )}
            </button>
            <button 
              type="button" 
              onClick={handleBackToLogin} 
              disabled={isLoading}
              className={`btn-secondary w-full ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              Back to Login
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Registration;
